/* public/js/ielts-scorer/charts.js — the charts of the Academic Task 1 practice
 * questions (public/data/ielts/bank.json), drawn from their data.
 *
 * Why drawn and not shipped as pictures: the same numbers give three things at
 * once — the picture the candidate describes, a PNG for the examiner model that
 * reads images, and a plain-text table for the one that does not (the RU grader
 * is text-only, so without the numbers in words it could not check a single
 * figure the candidate quotes).
 *
 * The look is the exam paper's: white sheet, dark ink, light grid — in both
 * themes, as the question booklet is white whatever the screen.
 *
 *   IeltsCharts.svg(spec)            → '<svg …>' (self-contained, no external refs)
 *   IeltsCharts.png(spec, width)     → Promise<{data: base64, media: 'image/png'}>
 *   IeltsCharts.dataText(spec)       → 'Households with internet access (%): Canada — 2005: 62; …'
 *
 * window.IeltsCharts + module.exports (node --test).
 */
(function (root, factory) {
    'use strict';
    var C = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = C;
    if (typeof window !== 'undefined') window.IeltsCharts = C;
    else if (root) root.IeltsCharts = C;
})(typeof self !== 'undefined' ? self : this, function () {
'use strict';

var W = 640, H = 400;
var INK = '#1f2328', SOFT = '#57606a', GRID = '#d0d7de', PAPER = '#ffffff';
/* Distinct in colour AND in lightness, so a printout or a colour-blind reader
   still tells the series apart. */
var PAL = ['#1f5fbf', '#e07b1a', '#2e8b57', '#8250df', '#cf222e', '#6e7781'];
var FONT = "font-family:system-ui,-apple-system,'Segoe UI',Roboto,Arial,sans-serif";

function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
}
function fmt(v) { return (Math.round(v * 10) / 10).toString(); }
function niceMax(v) {
    var p = Math.pow(10, Math.floor(Math.log10(Math.max(v, 1)))), m = v / p;
    return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 2.5 ? 2.5 : m <= 5 ? 5 : 10) * p;
}
/* A scale a reader can read off: 4–7 gridlines at 1·2·2.5·5 × 10ⁿ, never
   «2.4, 4.8, 7.2». Returns {max, step}. */
function scale(spec) {
    var m = spec.max || 0;
    if (!m) spec.series.forEach(function (s) { s.data.forEach(function (v) { if (v > m) m = v; }); });
    var p = Math.pow(10, Math.floor(Math.log10(Math.max(m, 1e-9)))), best = null;
    [0.1, 0.2, 0.25, 0.5, 1, 2, 2.5, 5, 10].forEach(function (f) {
        var st = f * p, n = Math.ceil(m / st - 1e-9);
        if (n >= 4 && n <= 7 && !best) best = { max: n * st, step: st };
    });
    return best || { max: niceMax(m), step: niceMax(m) / 5 };
}
function maxOf(spec) { return scale(spec).max; }
function text(x, y, s, o) {
    o = o || {};
    return '<text x="' + x + '" y="' + y + '" font-size="' + (o.size || 13) + '" fill="' + (o.fill || INK) + '"'
        + (o.anchor ? ' text-anchor="' + o.anchor + '"' : '') + (o.weight ? ' font-weight="' + o.weight + '"' : '')
        + (o.rotate ? ' transform="rotate(' + o.rotate + ' ' + x + ' ' + y + ')"' : '') + '>' + esc(s) + '</text>';
}
function legend(series, y) {
    if (series.length < 2) return '';
    var out = '', widths = series.map(function (s) { return 26 + String(s.name).length * 7.2; });
    var total = widths.reduce(function (a, b) { return a + b + 14; }, -14), x = (W - total) / 2;
    series.forEach(function (s, i) {
        out += '<rect x="' + x + '" y="' + (y - 10) + '" width="14" height="10" rx="2" fill="' + PAL[i % PAL.length] + '"/>';
        out += text(x + 20, y, s.name, { size: 12.5, fill: SOFT });
        x += widths[i] + 14;
    });
    return out;
}
function frame(spec, body) {
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + W + ' ' + H + '" width="' + W + '" height="' + H
        + '" role="img" aria-label="' + esc(spec.title) + '" style="' + FONT + '">'
        + '<rect width="' + W + '" height="' + H + '" fill="' + PAPER + '"/>'
        + text(W / 2, 28, spec.title, { size: 15, anchor: 'middle', weight: 700 }) + body + '</svg>';
}

function axes(spec, plot) {
    var sc = scale(spec), max = sc.max, out = '', steps = Math.round(max / sc.step), i;
    for (i = 0; i <= steps; i++) {
        var v = max * i / steps, y = plot.y + plot.h - plot.h * i / steps;
        out += '<line x1="' + plot.x + '" x2="' + (plot.x + plot.w) + '" y1="' + y + '" y2="' + y + '" stroke="' + GRID + '" stroke-width="1"/>';
        out += text(plot.x - 8, y + 4, fmt(v), { size: 12, fill: SOFT, anchor: 'end' });
    }
    out += '<line x1="' + plot.x + '" x2="' + plot.x + '" y1="' + plot.y + '" y2="' + (plot.y + plot.h) + '" stroke="' + SOFT + '"/>';
    out += '<line x1="' + plot.x + '" x2="' + (plot.x + plot.w) + '" y1="' + (plot.y + plot.h) + '" y2="' + (plot.y + plot.h) + '" stroke="' + SOFT + '"/>';
    return { svg: out, max: max };
}

function bar(spec) {
    var plot = { x: 58, y: 48, w: W - 78, h: H - 128 }, ax = axes(spec, plot), out = ax.svg;
    var n = spec.cats.length, k = spec.series.length, gw = plot.w / n, bw = Math.min(34, (gw * 0.72) / k);
    spec.cats.forEach(function (c, ci) {
        var gx = plot.x + gw * ci + (gw - bw * k) / 2;
        spec.series.forEach(function (s, si) {
            var v = s.data[ci], h = plot.h * v / ax.max, x = gx + si * bw, y = plot.y + plot.h - h;
            out += '<rect x="' + x.toFixed(1) + '" y="' + y.toFixed(1) + '" width="' + (bw - 2).toFixed(1) + '" height="' + h.toFixed(1) + '" fill="' + PAL[si % PAL.length] + '"/>';
        });
        out += text(plot.x + gw * ci + gw / 2, plot.y + plot.h + 18, c, { size: 12.5, anchor: 'middle' });
    });
    return frame(spec, out + legend(spec.series, H - 22));
}

function hbar(spec) {
    var sc = scale(spec), plot = { x: 110, y: 50, w: W - 150, h: H - 96 }, max = sc.max, out = '', steps = Math.round(max / sc.step), i;
    var n = spec.cats.length, gh = plot.h / n, bh = Math.min(30, gh * 0.62);
    for (i = 0; i <= steps; i++) {
        var x = plot.x + plot.w * i / steps;
        out += '<line x1="' + x + '" x2="' + x + '" y1="' + plot.y + '" y2="' + (plot.y + plot.h) + '" stroke="' + GRID + '"/>';
        out += text(x, plot.y + plot.h + 18, fmt(max * i / steps), { size: 12, fill: SOFT, anchor: 'middle' });
    }
    spec.cats.forEach(function (c, ci) {
        var v = spec.series[0].data[ci], y = plot.y + gh * ci + (gh - bh) / 2, w = plot.w * v / max;
        out += '<rect x="' + plot.x + '" y="' + y.toFixed(1) + '" width="' + w.toFixed(1) + '" height="' + bh.toFixed(1) + '" fill="' + PAL[0] + '"/>';
        out += text(plot.x - 8, y + bh / 2 + 4, c, { size: 12.5, anchor: 'end' });
        out += text(plot.x + w + 6, y + bh / 2 + 4, fmt(v), { size: 12, fill: SOFT });
    });
    return frame(spec, out);
}

function line(spec) {
    var plot = { x: 58, y: 48, w: W - 86, h: H - 128 }, ax = axes(spec, plot), out = ax.svg;
    var n = spec.cats.length, step = plot.w / (n - 1);
    spec.cats.forEach(function (c, ci) { out += text(plot.x + step * ci, plot.y + plot.h + 18, c, { size: 12.5, anchor: 'middle' }); });
    spec.series.forEach(function (s, si) {
        var col = PAL[si % PAL.length], pts = s.data.map(function (v, i) { return [plot.x + step * i, plot.y + plot.h - plot.h * v / ax.max]; });
        out += '<polyline fill="none" stroke="' + col + '" stroke-width="2.6" stroke-linejoin="round" points="' + pts.map(function (p) { return p[0].toFixed(1) + ',' + p[1].toFixed(1); }).join(' ') + '"/>';
        pts.forEach(function (p) {
            // A different marker per series: the lines stay apart in black and white.
            if (si % 3 === 0) out += '<circle cx="' + p[0].toFixed(1) + '" cy="' + p[1].toFixed(1) + '" r="3.6" fill="' + col + '"/>';
            else if (si % 3 === 1) out += '<rect x="' + (p[0] - 3.4).toFixed(1) + '" y="' + (p[1] - 3.4).toFixed(1) + '" width="6.8" height="6.8" fill="' + col + '"/>';
            else out += '<path d="M' + p[0].toFixed(1) + ' ' + (p[1] - 4.4).toFixed(1) + 'l4 7.4h-8z" fill="' + col + '"/>';
        });
    });
    return frame(spec, out + legend(spec.series, H - 22));
}

function pie(spec) {
    var out = '', k = spec.series.length, r = k > 1 ? 104 : 124, cy = 200;
    var centers = k > 1 ? [W * 0.27, W * 0.73] : [W * 0.36];
    spec.series.slice(0, 2).forEach(function (s, si) {
        var cx = centers[si], total = s.data.reduce(function (a, b) { return a + b; }, 0), a0 = -Math.PI / 2;
        s.data.forEach(function (v, i) {
            var a1 = a0 + 2 * Math.PI * v / total, large = (a1 - a0) > Math.PI ? 1 : 0;
            var x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0), x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
            out += '<path d="M' + cx + ' ' + cy + 'L' + x0.toFixed(1) + ' ' + y0.toFixed(1) + 'A' + r + ' ' + r + ' 0 ' + large + ' 1 ' + x1.toFixed(1) + ' ' + y1.toFixed(1) + 'Z" fill="' + PAL[i % PAL.length] + '" stroke="#fff" stroke-width="1.5"/>';
            var am = (a0 + a1) / 2, big = v / total >= 0.06, lr = big ? r * 0.68 : r + 16;
            // Every value is printed — a 4 % slice is a figure the candidate
            // has to quote. Small ones sit just outside their slice.
            out += text((cx + lr * Math.cos(am)).toFixed(1), (cy + lr * Math.sin(am) + 4).toFixed(1), fmt(v) + (spec.unit === '%' ? '%' : ''),
                { size: 12.5, anchor: 'middle', fill: big ? '#fff' : INK, weight: 700 });
            a0 = a1;
        });
        if (k > 1) out += text(cx, cy + r + 24, s.name, { size: 14, anchor: 'middle', weight: 700 });
    });
    // Legend: categories, with the small slices' values (not printed on the pie).
    var lx = k > 1 ? 40 : W * 0.66, ly = k > 1 ? H - 40 : 120;
    spec.cats.forEach(function (c, i) {
        var x = k > 1 ? lx + (i % 3) * 196 : lx, y = k > 1 ? ly + Math.floor(i / 3) * 18 - 6 : ly + i * 24;
        out += '<rect x="' + x + '" y="' + (y - 10) + '" width="12" height="12" rx="2" fill="' + PAL[i % PAL.length] + '"/>';
        out += text(x + 18, y, c, { size: 12.5, fill: SOFT });
    });
    return frame(spec, out);
}

function table(spec) {
    var cols = [''].concat(spec.series.map(function (s) { return s.name; }));
    var rowH = 34, x0 = 60, colW = (W - 120) / cols.length, out = '';
    var y0 = Math.max(52, Math.round((H + 20 - rowH * (spec.cats.length + 1)) / 2));
    out += '<rect x="' + x0 + '" y="' + y0 + '" width="' + (W - 120) + '" height="' + rowH + '" fill="#eef2f7"/>';
    cols.forEach(function (c, i) { out += text(x0 + colW * i + (i ? colW / 2 : 12), y0 + 22, c, { size: 13.5, weight: 700, anchor: i ? 'middle' : null }); });
    spec.cats.forEach(function (c, r) {
        var y = y0 + rowH * (r + 1);
        out += '<line x1="' + x0 + '" x2="' + (W - 60) + '" y1="' + y + '" y2="' + y + '" stroke="' + GRID + '"/>';
        out += text(x0 + 12, y + 22, c, { size: 13.5 });
        spec.series.forEach(function (s, si) { out += text(x0 + colW * (si + 1) + colW / 2, y + 22, fmt(s.data[r]), { size: 13.5, anchor: 'middle' }); });
    });
    var yEnd = y0 + rowH * (spec.cats.length + 1);
    out += '<rect x="' + x0 + '" y="' + y0 + '" width="' + (W - 120) + '" height="' + (yEnd - y0) + '" fill="none" stroke="' + SOFT + '"/>';
    return frame(spec, out);
}

function svg(spec) {
    if (!spec || !spec.kind) return '';
    return ({ bar: bar, hbar: hbar, line: line, pie: pie, table: table }[spec.kind] || bar)(spec);
}

/* The numbers in words, for a grader that cannot see images — and appended to
   the question for every grader, so a misquoted figure is checkable. */
function dataText(spec) {
    if (!spec) return '';
    var unit = spec.unit === '%' ? '%' : (spec.unit ? ' ' + spec.unit : '');
    var rows = spec.cats.map(function (c, ci) {
        return c + ' — ' + spec.series.map(function (s) { return (spec.series.length > 1 ? s.name + ': ' : '') + fmt(s.data[ci]) + unit; }).join(', ');
    });
    return spec.title + '. ' + rows.join('; ') + '.';
}

function png(spec, width) {
    return new Promise(function (resolve, reject) {
        try {
            var w = width || 960, h = Math.round(w * H / W);
            var img = new Image();
            img.onload = function () {
                try {
                    var cv = document.createElement('canvas');
                    cv.width = w; cv.height = h;
                    var cx = cv.getContext('2d');
                    cx.fillStyle = PAPER; cx.fillRect(0, 0, w, h);
                    cx.drawImage(img, 0, 0, w, h);
                    var url = cv.toDataURL('image/png');
                    resolve({ data: url.slice(url.indexOf(',') + 1), media: 'image/png' });
                } catch (e) { reject(e); }
            };
            img.onerror = function () { reject(new Error('chart_raster_failed')); };
            img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg(spec));
        } catch (e) { reject(e); }
    });
}

return { svg: svg, png: png, dataText: dataText };
});
